// @flow

import React from 'react';
import { connect } from 'react-redux';

import {
  type ApiKeys,
  PaymentMethodsSection,
  PaymentMethodsSectionSkeleton,
  paymentSourceMethods,
} from '@zapier/payment';

import * as Account from 'app/entities/Account';
import DashboardActions from 'app/dashboard/DashboardActions';
import TrackActions from 'app/track/TrackActions';
import z from 'app/common/z';
import {
  getIntegerParam,
  getQueryParam,
} from 'app/common/utils/RouterSelectors';

import usePaymentSourceState from './usePaymentSourceState';

import type { Dispatch } from 'app/common/types';

type ContainerProps = {
  autoEdit?: boolean,
  editInModal?: boolean,
  onDone?: () => void,
};

type Props = {
  ...$Exact<ContainerProps>,
  accountId: number,
  apiKeys: ApiKeys,
  onSaveSuccess: () => void,
  onTrack: (string, ?{}) => void,
  startEdit: boolean,
};

const mapStateToProps = (state, props: ContainerProps) => {
  const accountId =
    getIntegerParam('accountId', state) ||
    Account.selectors.all.currentAccountId(state);

  const startEdit = Boolean(
    getQueryParam(state, 'section') === paymentSourceMethods.card ||
      props.autoEdit
  );

  return {
    accountId,
    apiKeys: {
      braintree: z.cfg.braintreePubApiKey,
      stripe: z.cfg.stripePubApiKey,
    },
    startEdit,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onTrack(eventName, data = {}) {
    dispatch(TrackActions.track(eventName, data));
  },
  onSaveSuccess() {
    // Fired after payment source is successfully saved
    // to trigger side-effects in app
    dispatch(DashboardActions.loadHeldReasons());
  },
});

const App = (props: Props) => {
  const {
    currentSource,
    error,
    forceCardUpdate,
    isEditing,
    isLoading,
    isSaving,
    method,
    onCancelEdit,
    onEdit,
    onError,
    onHideSuccessMessage,
    onSave,
    onSelectMethod,
    showSuccessMessage,
    supportedMethods,
  } = usePaymentSourceState({
    accountId: props.accountId,
    onDone: props.onDone,
    onSaveSuccess: props.onSaveSuccess,
    onTrack: props.onTrack,
    startEdit: props.startEdit,
  });

  if (isLoading) {
    return <PaymentMethodsSectionSkeleton />;
  }

  return (
    <PaymentMethodsSection
      apiKeys={props.apiKeys}
      currentMethod={method}
      editInModal={Boolean(props.editInModal)}
      error={error}
      forceCardUpdate={forceCardUpdate}
      hasPaymentSource={Boolean(currentSource)}
      isEditing={isEditing}
      isEditingInFlight={isSaving}
      onCancelEdit={onCancelEdit}
      onEdit={onEdit}
      onErrorEdit={onError}
      onHideSuccessMessage={onHideSuccessMessage}
      onSaveEdit={onSave}
      onSelectSource={onSelectMethod}
      savedSource={currentSource}
      showChangeSuccessMessage={showSuccessMessage}
      supportedMethods={supportedMethods}
    />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
